import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Autocomplete,
  Alert,
  Divider,
  Card,
  CardContent,
} from "@mui/material";
import { Search, Download, Refresh } from "@mui/icons-material";
import axios from "axios";
import { format } from "date-fns";
import { AutoTaskAuthContext } from "../../Context/AutoTaskContext";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { useAccountsNav } from "../../utils/moduleNav";

import { useLedgerState } from "../../Context/LedgerStateContext";
import { useLocation } from "react-router-dom";

const ALL_ACCOUNTS_OPTION = {
  account_id: "all",
  account_name: "All",
  account_code: "",
};

const CashBankBook = ({ tabIndex }) => {
  // Keeps the voucher drill-down inside the host module (pawn keeps its chrome).
  const { go: goAccounts } = useAccountsNav();
  const location = useLocation();
  const { userInfo } = useContext(AutoTaskAuthContext) || {};
  const { ledgerState, updateTabState } = useLedgerState();
  const store_id = userInfo?.store_id;

  const isReturningFromEntry = location.state?.fromAccountsEntry === true;
  const highlightVoucher = location.state?.highlightVoucher || null;

  const savedState = isReturningFromEntry ? ledgerState.cashBankBook : null;

  // ── Ref to scroll highlighted row into view ──
  const highlightRowRef = useRef(null);

  const [selectedAccount, setSelectedAccount] = useState(
    savedState?.selectedAccount ?? null,
  );
  const [fromDate, setFromDate] = useState(
    savedState?.fromDate ??
      format(
        new Date(new Date().getFullYear(), new Date().getMonth(), 1),
        "yyyy-MM-dd",
      ),
  );
  const [toDate, setToDate] = useState(
    savedState?.toDate ?? format(new Date(), "yyyy-MM-dd"),
  );

  const [accounts, setAccounts] = useState([ALL_ACCOUNTS_OPTION]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);
  const [error, setError] = useState("");

  const [cashBankData, setCashBankData] = useState(
    savedState?.cashBankData ?? null,
  );

  // ── Scroll to highlighted row after data loads ──
  useEffect(() => {
    if (highlightVoucher && cashBankData && highlightRowRef.current) {
      setTimeout(() => {
        highlightRowRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 150);
    }
  }, [highlightVoucher, cashBankData]);

  useEffect(() => {
    updateTabState(tabIndex, {
      selectedAccount,
      fromDate,
      toDate,
      cashBankData,
    });
  }, [
    selectedAccount,
    fromDate,
    toDate,
    cashBankData,
    tabIndex,
    updateTabState,
  ]);

  const fetchAccounts = async () => {
    try {
      setLoadingAccounts(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/accounts/accounts/${store_id}/accounts-only`,
      );
      if (response.data.success) {
        const bankAccounts = response.data.data.filter(
          (acc) => acc.is_bank_account === true || acc.is_bank_account === 1,
        );
        setAccounts([ALL_ACCOUNTS_OPTION, ...bankAccounts]);
      }
    } catch (err) {
      console.error("Error fetching accounts:", err);
      setError("Error loading accounts");
    } finally {
      setLoadingAccounts(false);
    }
  };

  useEffect(() => {
    if (store_id !== null && store_id !== undefined) {
      fetchAccounts();
    }
  }, [store_id]);

  const handleGenerateReport = async () => {
    setError("");
    setCashBankData(null);

    if (!selectedAccount) {
      setError("Please select an account");
      return;
    }

    if (!fromDate || !toDate) {
      setError("Please select a date range");
      return;
    }

    try {
      setLoadingReport(true);

      const payload = {
        store_id: parseInt(store_id),
        from_date: fromDate,
        to_date: toDate,
      };

      let response;

      if (selectedAccount.account_id === "all") {
        response = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/api/accounts/cash-bank-book/all`,
          payload,
        );
      } else {
        payload.account_id = selectedAccount.account_id;
        response = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/api/accounts/cash-bank-book/account`,
          payload,
        );
      }

      if (response.data.success) {
        setCashBankData(response.data.data);
      } else {
        setError(response.data.message || "Failed to generate report");
      }
    } catch (err) {
      console.error("Error generating cash/bank book:", err);
      setError(
        err.response?.data?.message || "Error generating cash/bank book",
      );
    } finally {
      setLoadingReport(false);
    }
  };

  const handleReset = () => {
    setSelectedAccount(null);
    setError("");
    setCashBankData(null);
    setFromDate(
      format(
        new Date(new Date().getFullYear(), new Date().getMonth(), 1),
        "yyyy-MM-dd",
      ),
    );
    setToDate(format(new Date(), "yyyy-MM-dd"));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const generatePDF = () => {
    if (!cashBankData || !cashBankData.entries) return;

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 10;

    const formatAmount = (amount) => {
      return new Intl.NumberFormat("en-IN", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount);
    };

    const storeName = userInfo?.store_name || "";

    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text(storeName, pageWidth / 2, 12, { align: "center" });

    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    const accountName =
      cashBankData.account_details?.account_name || "All Bank Accounts";
    doc.text(`Cash/Bank Book - ${accountName}`, pageWidth / 2, 18, {
      align: "center",
    });

    doc.setFontSize(8);
    const statementLine = `Period: ${format(
      new Date(fromDate),
      "dd-MM-yyyy",
    )} to ${format(new Date(toDate), "dd-MM-yyyy")}`;
    doc.text(statementLine, margin, 24);

    const isAllAccounts = selectedAccount?.account_id === "all";

    const tableHead = isAllAccounts
      ? [
          [
            "Date",
            "Voucher",
            "Bank Account",
            "Contra Account",
            "Narration",
            "Debit",
            "Credit",
            "Balance",
          ],
        ]
      : [
          [
            "Date",
            "Voucher",
            "Contra Account",
            "Narration",
            "Debit",
            "Credit",
            "Balance",
          ],
        ];

    const tableData = [];

    const openingBalanceType =
      cashBankData.opening_balance_type === "DEBIT" ? "Db" : "Cr";

    if (isAllAccounts) {
      tableData.push([
        "",
        "",
        "",
        "",
        "Opening Balance",
        "",
        "",
        `${formatAmount(cashBankData.opening_balance)}${openingBalanceType}`,
      ]);
    } else {
      tableData.push([
        "",
        "",
        "",
        "Opening Balance",
        "",
        "",
        `${formatAmount(cashBankData.opening_balance)}${openingBalanceType}`,
      ]);
    }

    cashBankData.entries.forEach((entry) => {
      const dateStr = format(new Date(entry.date), "dd-MM-yyyy");
      const debitStr =
        entry.debit_amount > 0 ? formatAmount(entry.debit_amount) : "";
      const creditStr =
        entry.credit_amount > 0 ? formatAmount(entry.credit_amount) : "";
      const balanceType = entry.balance_type === "DEBIT" ? "Db" : "Cr";
      const balanceStr = `${formatAmount(entry.running_balance)}${balanceType}`;

      const contraDisplay = entry.contra_account_code
        ? `${entry.contra_account_code} - ${entry.contra_account_name}`
        : entry.contra_account_name;

      if (isAllAccounts) {
        const bankDisplay = entry.bank_account_code
          ? `${entry.bank_account_code} - ${entry.bank_account_name}`
          : entry.bank_account_name;

        tableData.push([
          dateStr,
          entry.voucher_number || "",
          bankDisplay,
          contraDisplay,
          entry.narration || "",
          debitStr,
          creditStr,
          balanceStr,
        ]);
      } else {
        tableData.push([
          dateStr,
          entry.voucher_number || "",
          contraDisplay,
          entry.narration || "",
          debitStr,
          creditStr,
          balanceStr,
        ]);
      }
    });

    if (isAllAccounts) {
      tableData.push([
        "",
        "",
        "",
        "",
        "Closing Totals",
        formatAmount(cashBankData.total_debit),
        formatAmount(cashBankData.total_credit),
        "",
      ]);
    } else {
      tableData.push([
        "",
        "",
        "",
        "Closing Totals",
        formatAmount(cashBankData.total_debit),
        formatAmount(cashBankData.total_credit),
        "",
      ]);
    }

    doc.autoTable({
      startY: 28,
      head: tableHead,
      body: tableData,
      theme: "grid",
      styles: {
        fontSize: 7,
        cellPadding: 1.5,
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
        textColor: [0, 0, 0],
        overflow: "linebreak",
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        halign: "center",
        lineWidth: 0.3,
        fontSize: 8,
      },
      columnStyles: isAllAccounts
        ? {
            0: { halign: "left", cellWidth: 20 },
            1: { halign: "left", cellWidth: 25 },
            2: { halign: "left", cellWidth: 35 },
            3: { halign: "left", cellWidth: 35 },
            4: { halign: "left", cellWidth: 40 },
            5: { halign: "right", cellWidth: 25 },
            6: { halign: "right", cellWidth: 25 },
            7: { halign: "right", cellWidth: 25 },
          }
        : {
            0: { halign: "left", cellWidth: 25 },
            1: { halign: "left", cellWidth: 30 },
            2: { halign: "left", cellWidth: 50 },
            3: { halign: "left", cellWidth: 60 },
            4: { halign: "right", cellWidth: 30 },
            5: { halign: "right", cellWidth: 30 },
            6: { halign: "right", cellWidth: 35 },
          },
      bodyStyles: { textColor: [0, 0, 0] },
      didParseCell: function (data) {
        if (data.row.index === 0 && data.section === "body") {
          data.cell.styles.fontStyle = "bold";
        }
        if (
          data.row.index === tableData.length - 1 &&
          data.section === "body"
        ) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fillColor = [245, 245, 245];
        }
        const debitColIndex = isAllAccounts ? 5 : 4;
        if (
          data.column.index === debitColIndex &&
          data.cell.text[0] &&
          data.section === "body" &&
          data.row.index !== 0 &&
          data.row.index !== tableData.length - 1
        ) {
          data.cell.styles.textColor = [255, 0, 0];
        }
      },
      margin: { left: margin, right: margin },
    });

    const accountName2 =
      cashBankData.account_details?.account_name || "All Bank Accounts";
    const sanitizedAccountName = accountName2.replace(/[^a-zA-Z0-9]/g, "_");
    const filename = `CashBankBook_${sanitizedAccountName}_${format(
      new Date(fromDate),
      "dd-MM-yyyy",
    )}_to_${format(new Date(toDate), "dd-MM-yyyy")}.pdf`;

    doc.save(filename);
  };

  const handleRowDoubleClick = (voucherNo) => {
    if (voucherNo) {
      goAccounts("/accounts-entry", {
        state: { voucher_no: voucherNo, fromLedger: true },
      });
    }
  };

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
        Report Filters
      </Typography>

      <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
        <Grid item xs={12} sm={6} md={3}>
          <Autocomplete
            options={accounts}
            getOptionLabel={(option) =>
              option.account_id === "all"
                ? "All Bank Accounts"
                : `${option.account_name}${
                    option.account_code ? " - " + option.account_code : ""
                  }`
            }
            value={selectedAccount}
            onChange={(event, newValue) => setSelectedAccount(newValue)}
            loading={loadingAccounts}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Bank Account"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingAccounts ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <TextField
            fullWidth
            type="date"
            label="From Date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <TextField
            fullWidth
            type="date"
            label="To Date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={5}>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="contained"
              size="large"
              startIcon={
                loadingReport ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <Search />
                )
              }
              onClick={handleGenerateReport}
              disabled={loadingReport}
            >
              {loadingReport ? "Generating..." : "Generate"}
            </Button>

            <Button
              variant="outlined"
              size="large"
              startIcon={<Refresh />}
              onClick={handleReset}
            >
              Reset
            </Button>
          </Box>
        </Grid>
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {cashBankData && cashBankData.entries && (
        <Paper elevation={2} sx={{ p: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              Cash/Bank Book Statement
            </Typography>

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="body1">
                  <strong>Account:</strong>{" "}
                  {cashBankData.account_details?.account_name}
                  {cashBankData.account_details?.account_code &&
                    ` (${cashBankData.account_details.account_code})`}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body1">
                  <strong>Period:</strong>{" "}
                  {format(new Date(fromDate), "dd/MM/yyyy")} to{" "}
                  {format(new Date(toDate), "dd/MM/yyyy")}
                </Typography>
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Opening Balance
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {formatCurrency(cashBankData.opening_balance)}
                  </Typography>
                  <Box
                    sx={{
                      mt: 1,
                      display: "inline-block",
                      px: 1,
                      py: 0.2,
                      borderRadius: 1,
                      fontSize: "1rem",
                      fontWeight: 600,
                      color: "white",
                      backgroundColor: "primary.main",
                    }}
                  >
                    {cashBankData.opening_balance_type}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Total Debit
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 600, color: "error.main" }}
                  >
                    {formatCurrency(cashBankData.total_debit)}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 1, display: "block" }}
                  >
                    {cashBankData.entry_count} entries
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Total Credit
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 600, color: "text.primary" }}
                  >
                    {formatCurrency(cashBankData.total_credit)}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 1, display: "block" }}
                  >
                    {cashBankData.entry_count} entries
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Closing Balance
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {formatCurrency(cashBankData.closing_balance)}
                  </Typography>
                  <Box
                    sx={{
                      mt: 1,
                      display: "inline-block",
                      px: 1,
                      py: 0.2,
                      borderRadius: 1,
                      fontSize: "1rem",
                      fontWeight: 600,
                      color: "white",
                      backgroundColor: "primary.main",
                    }}
                  >
                    {cashBankData.closing_balance_type}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "grey.100" }}>
                  <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Voucher No.</TableCell>
                  {selectedAccount?.account_id === "all" && (
                    <TableCell sx={{ fontWeight: 600 }}>Bank Account</TableCell>
                  )}
                  <TableCell sx={{ fontWeight: 600 }}>Contra Account</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Narration</TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontWeight: 600, color: "error.main" }}
                  >
                    Debit
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontWeight: 600, color: "text.primary" }}
                  >
                    Credit
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Balance
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Opening Balance Row */}
                <TableRow sx={{ backgroundColor: "grey.50" }}>
                  <TableCell
                    colSpan={selectedAccount?.account_id === "all" ? 5 : 4}
                  >
                    <strong>Opening Balance</strong>
                  </TableCell>
                  <TableCell align="right">-</TableCell>
                  <TableCell align="right">-</TableCell>
                  <TableCell align="right">
                    <Typography>
                      {formatCurrency(cashBankData.opening_balance)}{" "}
                    </Typography>
                    <Box
                      component="span"
                      sx={{
                        display: "inline-block",
                        ml: 0.5,
                        px: 0.5,
                        py: 0.5,
                        borderRadius: 20,
                        fontSize: "0.6rem",
                        fontWeight: 600,
                        lineHeight: 1,
                        color: "white",
                        backgroundColor: "primary.main",
                      }}
                    >
                      {cashBankData.opening_balance_type}
                    </Box>
                  </TableCell>
                </TableRow>

                {/* Transaction Rows */}
                {cashBankData.entries.map((entry, index) => {
                  const isHighlighted =
                    highlightVoucher === entry.voucher_number;
                  return (
                    <TableRow
                      key={index}
                      ref={isHighlighted ? highlightRowRef : null}
                      hover
                      onDoubleClick={() =>
                        handleRowDoubleClick(entry.voucher_number)
                      }
                      sx={{
                        cursor: "pointer",
                        backgroundColor: isHighlighted
                          ? "rgba(4, 126, 207, 0.15)"
                          : "transparent",
                        "&:hover": {
                          backgroundColor: isHighlighted
                            ? "rgba(4, 126, 207, 0.25)"
                            : "action.hover",
                        },
                      }}
                    >
                      <TableCell>
                        {format(new Date(entry.date), "dd/MM/yyyy")}
                      </TableCell>
                      <TableCell>{entry.voucher_number}</TableCell>
                      {selectedAccount?.account_id === "all" && (
                        <TableCell>
                          {entry.bank_account_code
                            ? `${entry.bank_account_code} - `
                            : ""}
                          {entry.bank_account_name}
                        </TableCell>
                      )}
                      <TableCell>
                        {entry.contra_account_code
                          ? `${entry.contra_account_code} - `
                          : ""}
                        {entry.contra_account_name}
                      </TableCell>
                      <TableCell>{entry.narration}</TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: "error.main", fontWeight: 500 }}
                      >
                        {entry.debit_amount > 0
                          ? formatCurrency(entry.debit_amount)
                          : "-"}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: "text.primary", fontWeight: 500 }}
                      >
                        {entry.credit_amount > 0
                          ? formatCurrency(entry.credit_amount)
                          : "-"}
                      </TableCell>
                      <TableCell align="right">
                        <strong>{formatCurrency(entry.running_balance)}</strong>{" "}
                        <Box
                          component="span"
                          sx={{
                            display: "inline-block",
                            ml: 0.5,
                            px: 0.5,
                            py: 0.5,
                            borderRadius: 20,
                            fontSize: "0.6rem",
                            fontWeight: 600,
                            lineHeight: 1,
                            color: "white",
                            backgroundColor: "primary.main",
                          }}
                        >
                          {entry.balance_type}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}

                {/* Closing Balance Row */}
                <TableRow sx={{ backgroundColor: "grey.50" }}>
                  <TableCell
                    colSpan={selectedAccount?.account_id === "all" ? 5 : 4}
                  >
                    <strong>Closing Balance</strong>
                  </TableCell>
                  <TableCell align="right" sx={{ color: "error.main" }}>
                    <strong>{formatCurrency(cashBankData.total_debit)}</strong>
                  </TableCell>
                  <TableCell align="right" sx={{ color: "text.primary" }}>
                    <strong>{formatCurrency(cashBankData.total_credit)}</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>
                      {formatCurrency(cashBankData.closing_balance)}{" "}
                      <Box
                        component="span"
                        sx={{
                          display: "inline-block",
                          ml: 0.5,
                          px: 0.5,
                          py: 0.5,
                          borderRadius: 20,
                          fontSize: "0.6rem",
                          fontWeight: 600,
                          lineHeight: 1,
                          color: "white",
                          backgroundColor: "primary.main",
                        }}
                      >
                        {cashBankData.closing_balance_type}
                      </Box>
                    </strong>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 3, textAlign: "right" }}>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={generatePDF}
            >
              Download PDF
            </Button>
          </Box>
        </Paper>
      )}

      {!cashBankData && !error && !loadingReport && (
        <Paper elevation={1} sx={{ p: 6, textAlign: "center" }}>
          <Typography variant="h6" color="text.secondary">
            Select a bank account and date range, then click "Generate" to view
            the cash/bank book
          </Typography>
        </Paper>
      )}
    </Paper>
  );
};

export default CashBankBook;
